import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, dateFmt, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { Phone, User, CalendarBlank, Bed, NotePencil, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";

const blank = { guest_name: "", phone: "", email: "", check_in: "", check_out: "", rooms_requested: 1, notes: "" };

export default function FrontDeskDashboard() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [f, setF] = useState(blank);
  const F = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const { data: enquiries = [] } = useQuery({ queryKey: ["fdEnquiries"], queryFn: api.frontDeskEnquiries });

  const mut = useMutation({
    mutationFn: (body) => api.frontDeskEnquiry(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["fdEnquiries"] }); toast.success("Enquiry logged — the sales team has it now"); setF(blank); },
    onError: (e) => toast.error(errMsg(e, "Could not save enquiry")),
  });

  const submit = (e) => {
    e.preventDefault();
    if (!f.guest_name.trim()) return toast.error("Guest name is required");
    if (!f.phone.trim()) return toast.error("Phone number is required");
    mut.mutate({ ...f, rooms_requested: Number(f.rooms_requested) || 1 });
  };

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-8">
      <span className="overline text-[#002FA7]">Front Desk</span>
      <h1 className="font-display font-black text-4xl text-[#111827] mt-2">New guest enquiry</h1>
      <p className="text-[#4B5563] mt-1">{user.company_name} · take a call and pass it straight to the sales team.</p>

      <div className="grid lg:grid-cols-2 gap-8 mt-8">
        <form onSubmit={submit} className="border border-[#111827] p-6 space-y-4" data-testid="fd-form">
          <label className="block text-sm"><span className="overline text-[#4B5563] mb-1 flex items-center gap-1"><User size={13} /> Guest name</span>
            <input data-testid="fd-guest-name" value={f.guest_name} onChange={F("guest_name")} className="w-full border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <div className="grid grid-cols-2 gap-4">
            <label className="block text-sm"><span className="overline text-[#4B5563] mb-1 flex items-center gap-1"><Phone size={13} /> Phone</span>
              <input data-testid="fd-phone" value={f.phone} onChange={F("phone")} className="w-full border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
            <label className="block text-sm"><span className="overline text-[#4B5563] mb-1 block">Email (optional)</span>
              <input data-testid="fd-email" value={f.email} onChange={F("email")} className="w-full border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <label className="block text-sm"><span className="overline text-[#4B5563] mb-1 flex items-center gap-1"><CalendarBlank size={13} /> Check-in</span>
              <input data-testid="fd-checkin" type="date" value={f.check_in} onChange={F("check_in")} className="w-full border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
            <label className="block text-sm"><span className="overline text-[#4B5563] mb-1 flex items-center gap-1"><CalendarBlank size={13} /> Check-out</span>
              <input data-testid="fd-checkout" type="date" value={f.check_out} onChange={F("check_out")} className="w-full border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          </div>
          <label className="block text-sm"><span className="overline text-[#4B5563] mb-1 flex items-center gap-1"><Bed size={13} /> Rooms requested</span>
            <input data-testid="fd-rooms" type="number" min="1" value={f.rooms_requested} onChange={F("rooms_requested")} className="w-full border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <label className="block text-sm"><span className="overline text-[#4B5563] mb-1 flex items-center gap-1"><NotePencil size={13} /> Notes</span>
            <textarea data-testid="fd-notes" rows={3} value={f.notes} onChange={F("notes")} placeholder="Company, event, rate mentioned, special requests…" className="w-full border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <button type="submit" data-testid="fd-submit" disabled={mut.isPending}
            className="w-full bg-[#002FA7] text-white py-3 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2">
            <PaperPlaneTilt size={18} weight="bold" /> {mut.isPending ? "Saving…" : "Send to sales team"}
          </button>
        </form>

        <div>
          <h2 className="font-display font-bold text-lg text-[#111827] mb-3">Recent enquiries</h2>
          <div className="space-y-3" data-testid="fd-enquiries">
            {enquiries.length === 0 && <div className="border border-dashed border-[#E5E7EB] py-14 text-center text-[#4B5563]">No enquiries yet. Log your first one on the left.</div>}
            {enquiries.map((l) => (
              <div key={l.id} className="rb-card p-4" data-testid={`fd-enquiry-${l.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-display font-bold text-[#111827]">{l.guest_name || l.company_name}</span>
                  {l.assigned_to_name
                    ? <span className="text-xs bg-[#DCFCE7] text-[#047857] px-2 py-0.5 font-semibold flex items-center gap-1"><CheckCircle size={12} weight="fill" /> {l.assigned_to_name}</span>
                    : <span className="text-xs bg-[#FEF3C7] text-[#92400E] px-2 py-0.5 font-semibold">Awaiting sales</span>}
                </div>
                <div className="text-sm text-[#4B5563] mt-1">{l.phone}{l.check_in ? ` · ${dateFmt(l.check_in)}–${dateFmt(l.check_out)}` : ""} · {l.rooms_requested || 1} rooms</div>
                {l.notes && <div className="text-xs text-[#9CA3AF] mt-1">{l.notes}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
